import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

const TYPES = [["percentage", "Percentage %"], ["flat", "Flat ₹ Off"], ["free_shipping", "Free Shipping"]];

function Editor({ item, onClose, onSaved }) {
  const [f, setF] = useState(item || { code: "", type: "percentage", value: "", min_purchase: "", max_discount: "", usage_limit: "", first_order_only: false, applies_to: "all", start: "", end: "", active: true });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = { ...f, value: Number(f.value || 0), min_purchase: Number(f.min_purchase || 0), max_discount: Number(f.max_discount || 0), usage_limit: Number(f.usage_limit || 0) };
      if (item?.id) await api.put(`/admin/discounts/${item.id}`, payload);
      else await api.post("/admin/discounts", payload);
      toast.success("Discount saved");
      onSaved();
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" data-testid="discount-editor">
      <div className="bg-[#FAF8F5] max-w-xl mx-auto lg:my-10 min-h-screen lg:min-h-0 p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-2xl">{item?.id ? "Edit" : "New"} Discount</h2>
          <button onClick={onClose} aria-label="Close"><X size={20} /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div><label className="tq-label">Coupon Code *</label>
            <input required value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} data-testid="discount-code" className="tq-input uppercase" placeholder="FESTIVE20" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="tq-label">Type</label>
              <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="discount-type" className="tq-input bg-white">
                {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
            {f.type !== "free_shipping" && (
              <div><label className="tq-label">Value {f.type === "percentage" ? "(%)" : "(₹)"}</label>
                <input required type="number" min="0" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="discount-value" className="tq-input" /></div>
            )}
            <div><label className="tq-label">Min Purchase (₹)</label>
              <input type="number" min="0" value={f.min_purchase} onChange={(e) => setF({ ...f, min_purchase: e.target.value })} className="tq-input" /></div>
            {f.type === "percentage" && (
              <div><label className="tq-label">Max Discount (₹)</label>
                <input type="number" min="0" value={f.max_discount} onChange={(e) => setF({ ...f, max_discount: e.target.value })} className="tq-input" /></div>
            )}
            <div><label className="tq-label">Usage Limit (0 = unlimited)</label>
              <input type="number" min="0" value={f.usage_limit} onChange={(e) => setF({ ...f, usage_limit: e.target.value })} className="tq-input" /></div>
            <div><label className="tq-label">Start (optional)</label>
              <input type="datetime-local" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="tq-input" /></div>
            <div><label className="tq-label">Expiry (optional)</label>
              <input type="datetime-local" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="discount-end" className="tq-input" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.first_order_only} onChange={(e) => setF({ ...f, first_order_only: e.target.checked })} className="accent-[#D4AF37] w-4 h-4" /> First order only</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="discount-active" className="accent-[#D4AF37] w-4 h-4" /> Active</label>
          <button type="submit" disabled={busy} data-testid="discount-save" className="tq-btn-dark w-full">{busy ? "Saving…" : "Save Discount"}</button>
        </form>
      </div>
    </div>
  );
}

export default function AdminDiscounts() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/discounts").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete this discount?")) return; await api.delete(`/admin/discounts/${id}`); toast.success("Deleted"); load(); };
  return (
    <div data-testid="admin-discounts">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl">Discounts & Coupons</h1>
        <button onClick={() => setEditing({})} data-testid="discount-create-btn" className="tq-btn-dark !py-2.5"><Plus size={14} /> New Discount</button>
      </div>
      {!items ? <div className="h-48 bg-white animate-pulse" /> : items.length === 0 ? (
        <div className="bg-white border border-[#E5DEC9] p-12 text-center text-[#6E6D6A]">No discounts yet.</div>
      ) : (
        <div className="bg-white border border-[#E5DEC9] overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[#8C7A5E] border-b border-[#E5DEC9]">
              <th className="p-3">Code</th><th className="p-3">Offer</th><th className="p-3">Conditions</th><th className="p-3">Used</th><th className="p-3">Status</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-b border-[#F4EFE6]" data-testid={`discount-row-${d.code}`}>
                  <td className="p-3 font-bold tracking-wider">{d.code}</td>
                  <td className="p-3">{d.type === "percentage" ? `${d.value}% off${d.max_discount ? ` (max ₹${d.max_discount})` : ""}` : d.type === "flat" ? `₹${d.value} off` : "Free shipping"}</td>
                  <td className="p-3 text-xs text-[#6E6D6A]">{d.min_purchase ? `Min ₹${d.min_purchase}` : "No minimum"}{d.first_order_only ? " · First order" : ""}{d.end ? ` · Ends ${new Date(d.end).toLocaleDateString("en-IN")}` : ""}</td>
                  <td className="p-3">{d.used_count}{d.usage_limit ? `/${d.usage_limit}` : ""}</td>
                  <td className="p-3"><span className={`text-[10px] font-bold uppercase px-2 py-1 ${d.active ? "bg-[#1A3C34]/10 text-[#1A3C34]" : "bg-[#F4EFE6] text-[#8C7A5E]"}`}>{d.active ? "Active" : "Off"}</span></td>
                  <td className="p-3"><div className="flex gap-2">
                    <button onClick={() => setEditing(d)} aria-label="Edit" data-testid={`discount-edit-${d.code}`} className="p-1.5 hover:text-[#C5A059]"><Pencil size={15} /></button>
                    <button onClick={() => del(d.id)} aria-label="Delete" className="p-1.5 hover:text-[#8B1E21]"><Trash2 size={15} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <Editor item={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
